import { Copy, Eye } from 'lucide-react';
import { useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import type { RevealPurpose } from '../api/types';
import { strings } from '../strings';
import { locationEntry } from './registry';
import { REASON_MAX, type RevealLocation } from './reveal';

const r = strings.reveal;
const FOCUSABLE = 'button:not([disabled]), input:not([disabled]), [tabindex="0"]';

export interface RevealChoice {
  reason: string;
  dontAsk: boolean;
}

/**
 * The reveal confirmation (spec 6.10.18): a popover anchored to the cell that says what is recorded and read,
 * with the reason and "Don't ask again". `onConfirm` runs inside the click or key event, so a copy can write
 * the clipboard in the same user gesture.
 */
export function RevealConfirm({
  name,
  location,
  account,
  purpose,
  anchor,
  required,
  problems,
  busy,
  initialReason = '',
  onConfirm,
  onCancel,
}: {
  name: string;
  location: RevealLocation;
  account: string | null;
  purpose: RevealPurpose;
  anchor: HTMLElement | null;
  /** The installation requires a reason (learned from the server). */
  required: boolean;
  /** Problems with the reason from the server's 400. */
  problems: readonly string[];
  busy: boolean;
  initialReason?: string;
  onConfirm: (choice: RevealChoice) => void;
  onCancel: () => void;
}) {
  const ids = useId();
  const panel = useRef<HTMLDivElement>(null);
  const field = useRef<HTMLInputElement>(null);
  const [reason, setReason] = useState(initialReason);
  const [dontAsk, setDontAsk] = useState(false);
  const [missing, setMissing] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  const shownProblems = missing ? [r.reasonMissing] : problems;

  useLayoutEffect(() => {
    const place = () => {
      const width = Math.min(440, window.innerWidth - 16);
      const rect = anchor?.isConnected ? anchor.getBoundingClientRect() : null;
      const height = panel.current?.offsetHeight ?? 260;
      if (!rect) {
        setPosition({ top: 80, left: Math.max(8, (window.innerWidth - width) / 2), width });
        return;
      }
      let top = rect.bottom + 4;
      if (top + height > window.innerHeight - 8) top = Math.max(8, rect.top - height - 4);
      const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
      setPosition({ top, left, width });
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [anchor]);

  useEffect(() => {
    field.current?.focus();
    return () => {
      if (anchor?.isConnected) anchor.focus();
    };
  }, [anchor]);

  // A click outside closes it, like the Set value popover.
  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (panel.current && !panel.current.contains(event.target as Node)) onCancel();
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [onCancel]);

  const confirm = () => {
    if (busy) return;
    if (required && !reason.trim()) {
      setMissing(true);
      field.current?.focus();
      return;
    }
    onConfirm({ reason, dontAsk: dontAsk && !required });
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onCancel();
    } else if (event.key === 'Enter' && (event.target as HTMLElement).tagName === 'INPUT') {
      if ((event.target as HTMLInputElement).type === 'checkbox') return;
      event.preventDefault();
      confirm();
    } else if (event.key === 'Tab' && panel.current) {
      const items = Array.from(panel.current.querySelectorAll<HTMLElement>(FOCUSABLE));
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    }
    // Keys typed here never reach the table's grid keys or the page's shortcuts.
    event.stopPropagation();
  };

  const parts = locationEntry(location).revealText(location, account);
  const Icon = purpose === 'copy' ? Copy : Eye;
  return createPortal(
    <div
      ref={panel}
      role="dialog"
      aria-modal="false"
      aria-labelledby={`${ids}-title`}
      aria-describedby={`${ids}-text`}
      className="popover var-popover var-reveal-confirm"
      data-testid="reveal-confirm"
      style={{
        position: 'fixed',
        top: position?.top ?? -9999,
        left: position?.left ?? -9999,
        width: position?.width ?? 440,
      }}
      onKeyDown={onKeyDown}
    >
      <div className="flex items-center gap-2 px-3 pt-3">
        <Icon aria-hidden className="size-4 text-fg-muted" />
        <h3 id={`${ids}-title`} className="min-w-0 flex-1 truncate font-semibold">
          {purpose === 'copy' ? r.confirmCopyTitle(name) : r.confirmTitle(name)}
        </h3>
      </div>
      <div className="space-y-2 px-3 py-2">
        <p id={`${ids}-text`} className="text-[13px] leading-snug">
          {parts.map((part, i) => (typeof part === 'string' ? part : <strong key={i}>{part.strong}</strong>))}{' '}
          {purpose === 'copy' ? r.copyNotShown : r.hidesAfter}
        </p>
        <div>
          <label htmlFor={`${ids}-reason`} className="text-[13px]">
            <span className="font-semibold">{r.reason}</span>{' '}
            <span className="text-fg-muted">{required ? r.reasonRequired : r.reasonOptional}</span>
          </label>
          <input
            ref={field}
            id={`${ids}-reason`}
            className="input mt-1 w-full"
            value={reason}
            maxLength={REASON_MAX}
            autoComplete="off"
            placeholder={r.reasonPlaceholder}
            aria-required={required || undefined}
            aria-invalid={shownProblems.length ? true : undefined}
            aria-describedby={shownProblems.length ? `${ids}-problems` : undefined}
            onChange={(event) => {
              setReason(event.target.value);
              setMissing(false);
            }}
          />
          {shownProblems.length > 0 && (
            <p id={`${ids}-problems`} className="field-error" role="alert">
              {shownProblems.join(' ')}
            </p>
          )}
        </div>
      </div>
      <div className="flex items-center justify-end gap-2 border-t border-line px-3 py-2">
        {!required && (
          <label className="mr-auto flex items-center gap-1.5 text-[13px]">
            <input type="checkbox" checked={dontAsk} onChange={(event) => setDontAsk(event.target.checked)} />
            {r.dontAsk}
          </label>
        )}
        <button type="button" className="btn" onClick={onCancel}>
          {r.cancel}
        </button>
        <button type="button" className="btn btn-primary" disabled={busy} onClick={confirm}>
          {purpose === 'copy' ? r.confirmCopy : r.confirm}
        </button>
      </div>
    </div>,
    document.body,
  );
}
